import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerTitle,
} from '@rocco/components/drawer';
import { CLEARCUT_FILL_COLOR } from '@rocco/components/image';
import { ItemShippingProvider } from '@modules/ecommerce/context/ItemShippingContext';
import { VariantShippingMethodsProvider } from '~/modules/ecommerce/context/VariantShippingMethodsContext';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import SpecialtyPricingBanner from '@modules/product/component/specialty-pricing-banner/SpecialtyPricingBanner';
import TradeOnlyBanner from '@modules/product/component/trade-only-banner/TradeOnlyBanner';
import StockAndShipping from '@modules/product/component/variant-purchase/StockAndShipping';
import ProductLocationsDrawer from '~/modules/product/component/product-details/ProductLocationsDrawer';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { useProductEnquiryModal } from '../../hooks/use-product-enquiry-modal';
import { MobileVariantSelector } from './MobileVariantSelector';
import {
    InlineAddToCartButton,
    InlineSamplePurchaseButton,
    PILL,
    PurchasePriceBlock,
    SampleShippingInfo,
    QuantityStepper,
    useSizesInUse,
    useVariantEnquiryContext,
} from './purchase-flow-parts';

/** Cap on how many variant images to eagerly preload when the drawer opens. */
const VARIANT_IMAGE_PRELOAD_LIMIT = 16;

/** Fallback background for multiplied/clear-cut studio images (matches PhotoViewerModal). */
const MULTIPLIED_IMAGE_BG = CLEARCUT_FILL_COLOR;

export interface DesktopPurchaseDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    isBuyable: boolean;
    isSampleBuyable: boolean;
    /** Selected-variant price for the pinned footer — the pick changes the
     *  price, and the drawer covers the page's price block. Null hides it. */
    priceString: string | null;
    isPriceRange: boolean;
    isOnSale: boolean;
    retailPriceString: string | null;
    pricePrefix: string | null;
    /** Enquiry action label / modal title (e.g. "Enquire with Nero Tapware"). */
    enquiryLabel: string;
    /** Whether the enquiry CTA may show (the professional's audience gating). */
    showCTA: boolean;
    showSpecifierLoginPrompt: boolean;
}

/**
 * Desktop slide-in purchase drawer — the desktop counterpart to the mobile
 * variant sheet, with the same content order and footer: selected-variant
 * image (the page gallery sits behind the drawer), title, chip/swatch variant
 * selector with the same dynamic option enabling, stock/shipping for the
 * selection, then a pinned footer led by "Enquire about this configuration"
 * with commerce (quantity + Add to Cart / Order Sample) as the outlined
 * secondary. Where-to-buy and the specifier login prompt keep their only
 * desktop entry points here.
 *
 * Requires `VariantSelectionProvider` + `VariantOptionProvider` above it.
 */
export const DesktopPurchaseDrawer = ({
    open,
    onOpenChange,
    isBuyable,
    isSampleBuyable,
    priceString,
    isPriceRange,
    isOnSale,
    retailPriceString,
    pricePrefix,
    enquiryLabel,
    showCTA,
    showSpecifierLoginPrompt,
}: DesktopPurchaseDrawerProps) => {
    const { product, whereToBuyEnabled } = useLoaderData<typeof loader>();
    const { selectedVariant, quantity } = useVariantSelectionState();
    const { openEnquiryModal } = useProductEnquiryModal();
    const variantEnquiry = useVariantEnquiryContext();
    const [whereToBuyOpen, setWhereToBuyOpen] = useState(false);

    const sizesInUse = useSizesInUse();
    const isEnquiryLed = !isBuyable && !isSampleBuyable;

    // A single image tracking the selected variant (no carousel/peek). Matches
    // the variant's image against the gallery's LibraryImages (the same source
    // the gallery uses), falling back to the first product image.
    const variantImage =
        (selectedVariant?.Image &&
            product.LibraryImages.find(
                (image) => image.ID === selectedVariant.Image?.ID,
            )) ||
        product.LibraryImages[0];
    const imageSrc =
        variantImage?.LibraryImageFile?.DetailPageSquare.Desktop ?? '';

    // A6 — background colour for the drawer thumbnail. Multiplied/clear-cut
    // studio shots use a neutral background (ImageBackgroundColor or the clear-cut fill token)
    // instead of cropping to fill. Matches the PhotoViewerModal pattern.
    const isMultiplied = Boolean(variantImage?.IsMultiplied);
    const isClearCut = Boolean(variantImage?.IsClearCut);
    const useSoftBackground = isMultiplied || isClearCut;
    const imageBackgroundColor =
        variantImage?.LibraryImageFile?.ImageBackgroundColor ??
        MULTIPLIED_IMAGE_BG;

    // Preload variant images so switching colour swaps instantly instead of
    // waiting on a fresh network fetch. Capped so a high-colour product doesn't
    // fire dozens of fetches on open; colours past the cap fetch on selection.
    const variantImageSrcs = useMemo(() => {
        const variantImageIds = new Set(
            product.Variants.map((v) => v.Image?.ID).filter(
                (id): id is number => typeof id === 'number',
            ),
        );
        return product.LibraryImages.filter((image) =>
            variantImageIds.has(image.ID),
        )
            .map((image) => image.LibraryImageFile?.DetailPageSquare.Desktop)
            .filter((src): src is string => Boolean(src))
            .slice(0, VARIANT_IMAGE_PRELOAD_LIMIT);
    }, [product]);

    return (
        <>
            {/* handleOnly with no rendered handle (handles are bottom-only)
                disables drag entirely; overlay-click and the X still close it. */}
            <Drawer
                direction="right"
                handleOnly
                open={open}
                onOpenChange={onOpenChange}
            >
                <DrawerContent
                    // No drag handle on a right-side drawer (the handle slot
                    // otherwise mounts a stray pill at the top); scrollable makes
                    // DrawerContent's body wrapper a height-filling flex child so
                    // our inner column can scroll its content and pin the footer.
                    noHandle
                    scrollable
                    // 25.875rem (pxToRem(414)) so the drawer scales with the
                    // app's responsive root font-size, matching the rest of the
                    // page. Inline style (not a Tailwind class) because the base
                    // variant's max-w-sm sits under the same data+sm prefix and
                    // would win on CSS source order.
                    style={{ width: '25.875rem', maxWidth: '25.875rem' }}
                    // The close button now sits over the image — give it a
                    // backing so it stays legible.
                    closeButtonClassName="z-10 rounded-full bg-white/85 p-1.5 opacity-100 backdrop-blur-sm"
                    className={cn(
                        // vaul 1.x ships no side-drawer transition — add the slide
                        // in/out ourselves (the established rocco sheet pattern).
                        'data-[state=open]:animate-in data-[state=open]:duration-300 data-[state=open]:slide-in-from-right',
                        'data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=closed]:slide-out-to-right',
                    )}
                >
                    {/* Radix dialog a11y: a description silences the missing
                        aria-describedby warning; the visible title is below. */}
                    <DrawerDescription className="hidden" aria-hidden />

                    <VariantShippingMethodsProvider
                        selectedVariant={selectedVariant}
                        quantity={quantity}
                        estimatedSampleWeight={
                            product.Professional.SampleEstimatedWeight
                        }
                    >
                        <ItemShippingProvider>
                            <div className="flex h-full min-h-0 flex-col">
                                {/* Scrollable content — grows to fill, scrolls when
                                it exceeds the viewport height. */}
                                <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain pb-4">
                                    {imageSrc ? (
                                        // A6 — multiplied/clear-cut images sit on a
                                        // neutral background (contain + multiply
                                        // blend) to avoid cropping studio shots.
                                        // Plain photos use object-cover as before.
                                        <div
                                            className={cn(
                                                'aspect-square w-full shrink-0 overflow-hidden rounded-tile',
                                                useSoftBackground
                                                    ? 'flex items-center justify-center'
                                                    : '',
                                            )}
                                            style={
                                                useSoftBackground
                                                    ? {
                                                          backgroundColor:
                                                              imageBackgroundColor,
                                                      }
                                                    : {
                                                          backgroundColor:
                                                              '#f3f4f6',
                                                      }
                                            }
                                        >
                                            <img
                                                src={imageSrc}
                                                alt={
                                                    variantEnquiry.messageSubject
                                                }
                                                className={cn(
                                                    'size-full',
                                                    useSoftBackground
                                                        ? 'object-contain mix-blend-multiply'
                                                        : 'object-cover',
                                                )}
                                            />
                                        </div>
                                    ) : null}

                                    {/* Warm the cache for every variant image so
                                    colour switches are instant. */}
                                    <div className="hidden" aria-hidden="true">
                                        {variantImageSrcs.map((src) => (
                                            <img
                                                key={src}
                                                src={src}
                                                alt=""
                                                loading="eager"
                                            />
                                        ))}
                                    </div>

                                    <div className="px-4 pt-5">
                                        <DrawerTitle className="text-left font-sans text-xl leading-[1.2] font-medium tracking-[-0.02em] text-[#181818]">
                                            {product.Title}
                                        </DrawerTitle>

                                        <div className="mt-5 [&:empty]:hidden">
                                            <SpecialtyPricingBanner
                                                product={product}
                                            />
                                            <TradeOnlyBanner
                                                selectedVariant={
                                                    selectedVariant
                                                }
                                            />
                                        </div>

                                        <div className="mt-5">
                                            <MobileVariantSelector
                                                variants={product.Variants}
                                                sizes={sizesInUse}
                                            />
                                        </div>

                                        {/* The conversion-critical facts: stock,
                                            shipping cost and delivery time for
                                            the selection. */}
                                        {isBuyable ? (
                                            <div className="mt-6">
                                                <StockAndShipping
                                                    refundPolicyData={product}
                                                />
                                            </div>
                                        ) : null}
                                        {!isBuyable && isSampleBuyable ? (
                                            <div className="mt-6">
                                                <SampleShippingInfo />
                                            </div>
                                        ) : null}
                                    </div>
                                </div>

                                {/* Pinned footer, matching the mobile variant
                                    sheet: the selection's live price (the pick
                                    changes it, and the drawer covers the page's
                                    price block), then "Enquire about this
                                    configuration" (the user just told us exactly
                                    what they want — the strongest lead on the
                                    page), with commerce (quantity + Add to Cart /
                                    Order Sample) as the outlined secondary.
                                    Commerce only leads when enquiries are off. */}
                                <div className="flex shrink-0 flex-col gap-3 border-t border-black/10 px-4 pt-4 pb-6">
                                    <PurchasePriceBlock
                                        align="start"
                                        compact
                                        priceString={priceString}
                                        isPriceRange={isPriceRange}
                                        isInServiceableArea={
                                            product.IsInServiceableArea
                                        }
                                        isOnSale={isOnSale}
                                        retailPriceString={retailPriceString}
                                        pricePrefix={pricePrefix}
                                    />
                                    {showCTA ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onOpenChange(false);
                                                openEnquiryModal({
                                                    title: enquiryLabel,
                                                    ctaLabel: enquiryLabel,
                                                    messageSubject:
                                                        variantEnquiry.messageSubject,
                                                    itemImage:
                                                        variantEnquiry.itemImage,
                                                    brandImage:
                                                        variantEnquiry.brandImage,
                                                });
                                            }}
                                            className={PILL('filled-light')}
                                        >
                                            Enquire about this configuration
                                        </button>
                                    ) : null}
                                    {isBuyable ? (
                                        <div className="flex items-center gap-3">
                                            <QuantityStepper />
                                            <div className="min-w-0 flex-1">
                                                <InlineAddToCartButton
                                                    variant={
                                                        showCTA
                                                            ? 'outlined-light'
                                                            : 'filled-light'
                                                    }
                                                    onAdded={() =>
                                                        onOpenChange(false)
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ) : null}
                                    {isSampleBuyable ? (
                                        <InlineSamplePurchaseButton
                                            onAdded={() => onOpenChange(false)}
                                        />
                                    ) : null}
                                    {whereToBuyEnabled ? (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setWhereToBuyOpen(true)
                                            }
                                            className={PILL('outlined-light')}
                                        >
                                            Where to Buy
                                        </button>
                                    ) : null}
                                    {isEnquiryLed &&
                                    !showCTA &&
                                    showSpecifierLoginPrompt ? (
                                        <SpecifierLoginPrompt
                                            professionalName={
                                                product.Professional.Title
                                            }
                                            showBottomBorder={false}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </ItemShippingProvider>
                    </VariantShippingMethodsProvider>
                </DrawerContent>
            </Drawer>

            {whereToBuyEnabled ? (
                <ProductLocationsDrawer
                    product={product}
                    open={whereToBuyOpen}
                    toggleModal={setWhereToBuyOpen}
                />
            ) : null}
        </>
    );
};
